/**
 * Pure derivation of the brief "Mark as ready" disabled reason
 *
 *
 * Priority order — first match wins:
 *   1. submitted    → "Already marked as ready"
 *   2. !isValid     → "Fix validation errors first"
 *   3. isDirty      → "Save changes before marking as ready"
 *   4. updatePending → "Saving in progress…"
 *   5. otherwise    → null (enabled)
 *
 * Returns `null` only when the button should be enabled — the page uses
 * that signal to bind the click handler and skip the tooltip wrap.
 *
 * BE enum note: `status === 'submitted'` is unchanged; only the surfaced
 * copy ("Ready" / "marked as ready") was relabeled.
 */
import type { BriefStatus } from '@/entities/brief';
import { i18n } from '@/shared/i18n';

export interface DeriveSubmitDisabledReasonArgs {
  status: BriefStatus;
  isValid: boolean;
  isDirty: boolean;
  isUpdatePending: boolean;
}

export function deriveSubmitDisabledReason({
  status,
  isValid,
  isDirty,
  isUpdatePending,
}: DeriveSubmitDisabledReasonArgs): string | null {
  if (status === 'submitted')
    return i18n.t('projects:projectBriefDetail.deriveSubmitDisabledReason.alreadyMarkedAsReady');
  if (!isValid)
    return i18n.t(
      'projects:projectBriefDetail.deriveSubmitDisabledReason.fixValidationErrorsFirst',
    );
  if (isDirty)
    return i18n.t(
      'projects:projectBriefDetail.deriveSubmitDisabledReason.saveChangesBeforeMarkingAs',
    );
  if (isUpdatePending)
    return i18n.t('projects:projectBriefDetail.deriveSubmitDisabledReason.savingInProgress');
  return null;
}
